// F16 — the one place the App Store / Google Play badge rules live.
//
// Apple and Google both require their own artwork used unmodified: no
// redrawing, no recolouring, no different corner radius, no shrinking below
// their minimum height, and neither badge more prominent than the other.
// So the Apple badge is Apple's own SVG, rendered at their proportions
// (119.66 x 40 at 1x, never below 40px tall).
//
// Google Play is NOT live — both Android apps have been in review since
// 2026-08-25 — and Google expects the badge to link to a real listing. A
// dead badge is not an option, and dimming the badge is itself a
// modification their guidelines discourage. So the Play slot renders OUR OWN
// element: same box, same height, our typeface, reading "coming soon".
// Because it is not Google's artwork, no guideline is being modified.
// Setting STORE_LINKS.play swaps it for the real badge.
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { STORE_LINKS, APPLE_BADGE, PLAY_BADGE } from './storeLinks';
import { C, FONT, R, SP } from './tokens';

// Apple's badge is specified at 119.66 x 40. Keep the ratio; never go below
// their 40px minimum height.
const BADGE_HEIGHT = 44;
const APPLE_RATIO = 119.66 / 40;

const StoreBadges = ({ app = 'pos', align = 'start' }) => {
    const { t, language } = useLanguage();

    const appleHref = app === 'manager' ? STORE_LINKS.appStoreManager : STORE_LINKS.appStorePos;
    const appleArtwork = language === 'ar' ? APPLE_BADGE.ar : APPLE_BADGE.en;
    const playArtwork = language === 'ar' ? PLAY_BADGE.ar : PLAY_BADGE.en;

    const boxHeight = `${BADGE_HEIGHT}px`;

    const row = {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : 'flex-start',
        gap: SP.md,
    };

    const anchor = {
        height: boxHeight,
        display: 'inline-block',
        lineHeight: 0,
    };

    return (
        <div style={row}>
            <a
                data-store="app-store"
                href={appleHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('Download Aqana on the App Store')}
                style={anchor}
            >
                <img
                    src={appleArtwork}
                    alt={t('Download on the App Store')}
                    width={Math.round(BADGE_HEIGHT * APPLE_RATIO)}
                    height={BADGE_HEIGHT}
                    style={{ height: boxHeight, width: 'auto', display: 'block' }}
                />
            </a>

            {STORE_LINKS.play ? (
                <a
                    data-store="play"
                    href={STORE_LINKS.play}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('Get Aqana on Google Play')}
                    style={anchor}
                >
                    <img
                        src={playArtwork}
                        alt={t('Get it on Google Play')}
                        height={BADGE_HEIGHT}
                        style={{ height: boxHeight, width: 'auto', display: 'block' }}
                    />
                </a>
            ) : (
                <span
                    data-store="play"
                    style={{
                        height: boxHeight,
                        display: 'inline-flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                        background: 'transparent',
                        border: `1px solid ${C.lineStrong}`,
                        borderRadius: R.md,
                        paddingInline: SP.lg,
                        boxSizing: 'border-box',
                        fontFamily: FONT.body,
                        color: C.inkFaint,
                        textAlign: 'start',
                    }}
                >
                    <span style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {t('Android')}
                    </span>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: C.inkMuted }}>
                        {t('Coming soon')}
                    </span>
                </span>
            )}
        </div>
    );
};

export default StoreBadges;
